import { computed, makeObservable } from 'mobx'
import type { MobxDocument } from '../firestore-mobx'
import {
  ObservableModelWithDecorators,
  emptyCollection,
  emptyModel,
} from '../firestore-mobx/model'
import {
  empty,
  type FirestoreSectionAssignment,
} from '../firestore/SectionAssignment'
import { buildSlideDeckObservableModelDocument } from '../firestore/SlideDeck'
import type { FirebaseRepository } from './FirebaseRepository'
import { DateTime } from 'luxon'
import {
  AssignmentState,
  AssignmentStatus,
  AssignmentStatusOld,
} from '../types'
import { GROUPING_SIZE_MAXIMUM_DEFAULT } from '../constants'
import { getAssignmentStatus } from '../sources-of-truth/assignmentStatus'
import { dateTime } from '../util'

export type ScalarsType = FirestoreSectionAssignment['assignmentGradingScalars']

export class SectionAssignment extends ObservableModelWithDecorators<FirestoreSectionAssignment> {
  static empty(repository: FirebaseRepository) {
    return emptyModel(repository, this, empty)
  }

  static emptyCollection(repository: FirebaseRepository) {
    return emptyCollection(repository, this, empty)
  }

  constructor(
    repository: FirebaseRepository,
    doc: MobxDocument<FirestoreSectionAssignment>
  ) {
    super(repository, doc)

    makeObservable(this)
  }

  @computed
  get sectionId() {
    return this.unsafeData?.sectionId ?? ''
  }

  @computed
  get expiresAt() {
    return this.unsafeData?.expiresAt ?? undefined
  }

  @computed
  get assignedAt() {
    return this.unsafeData?.assignedAt ?? undefined
  }

  @computed
  get assignmentOverdueAt() {
    return this.unsafeData?.assignmentOverdueAt ?? undefined
  }

  /** If the assignment allows late submissions, this field is filled with expiresAt. */
  @computed
  get extendedDueDate() {
    return this.assignmentOverdueAt ? this.expiresAt : undefined
  }

  /** The date students are told to submit their assignments by. expiresAt may be later if late submissions are allowed. */
  @computed
  get dueDate() {
    const hasLateSubmissions = !!this.assignmentOverdueAt

    if (hasLateSubmissions && this.assignmentOverdueAt) {
      return this.assignmentOverdueAt
    } else if (this.expiresAt) {
      return this.expiresAt
    } else {
      return undefined
    }
  }

  @computed
  get dueDateTime() {
    return dateTime(this.dueDate)
  }

  groupCompletionWasLate = ({
    roomStartedAt,
  }: {
    roomStartedAt: Date | undefined
  }) => {
    if (!roomStartedAt) return false

    if (!this.assignmentOverdueAt) return false

    return roomStartedAt.getTime() > this.assignmentOverdueAt.getTime()
  }

  @computed
  get assignmentStatus(): AssignmentStatus {
    return getAssignmentStatus(this.data)
  }

  // TODO: Remove when newInstructorPages flag is removed
  @computed
  get assignmentStatusOld(): AssignmentStatusOld {
    // milliseconds in a day
    const oneDay = 1000 * 60 * 60 * 24
    if (this.expiresAt && this.expiresAt.getTime() + oneDay > Date.now()) {
      return AssignmentStatusOld.pending
    }
    return AssignmentStatusOld.completed
  }

  @computed
  get expiresAtDate() {
    return this.expiresAt ? DateTime.fromJSDate(this.expiresAt) : undefined
  }

  @computed
  get assignedAtDate() {
    return this.assignedAt ? DateTime.fromJSDate(this.assignedAt) : undefined
  }

  @computed
  get assignedAtIsInFuture() {
    return this.assignedAt ? this.assignedAt.getTime() > Date.now() : false
  }

  @computed
  get expiresAtIsInPast() {
    return this.expiresAt ? this.expiresAt.getTime() < Date.now() : false
  }

  @computed
  get isCancelled() {
    return this.data.assignmentState === AssignmentState.canceled
  }

  @computed
  get isDraft() {
    return this.data.assignmentState === AssignmentState.draft
  }

  @computed
  get isUnconfigured() {
    return this.data.assignmentState === AssignmentState.unconfigured
  }

  @computed
  get isActive() {
    return this.data.assignmentState === AssignmentState.active
  }

  @computed
  get formattedAssignedAt() {
    // EX. Oct 18, 2024
    return this.assignedAt
      ? DateTime.fromJSDate(this.assignedAt).toFormat('LLL dd, yyyy')
      : ''
  }

  @computed
  get formattedAssignedAtWithTime() {
    // EX. Oct 18, 2024 12:00 PM
    return this.assignedAt
      ? DateTime.fromJSDate(this.assignedAt).toFormat('LLL dd, yyyy h:mm a')
      : ''
  }

  @computed
  get formattedExpiresAt() {
    return this.expiresAt
      ? DateTime.fromJSDate(this.expiresAt).toFormat('LLL dd, yyyy')
      : ''
  }

  @computed
  get formattedExpiresAtWithTime() {
    return this.expiresAt
      ? DateTime.fromJSDate(this.expiresAt).toFormat('LLL dd, yyyy hh:mm a')
      : ''
  }

  @computed
  get formattedDueDate() {
    return this.dueDate
      ? DateTime.fromJSDate(this.dueDate).toFormat('LLL dd, yyyy')
      : ''
  }

  @computed
  get formattedDueDateWithTime() {
    return this.dueDate
      ? DateTime.fromJSDate(this.dueDate).toFormat('LLL dd, yyyy hh:mm a')
      : ''
  }

  @computed
  get isExpired() {
    return this.expiresAt
      ? this.expiresAt.getTime() < this.repository.currentMinute.toMillis()
      : false
  }

  @computed
  get slideDeck() {
    return buildSlideDeckObservableModelDocument(
      this.repository,
      this.data?.slideDeckId
    )?.model
  }

  @computed
  get assignmentType(): AssignmentType | undefined {
    if (!this.hasData) return undefined
    // return index of the rubric type enum
    switch (this.data.assignmentType) {
      case 0:
        return AssignmentType.ManyGroups
      case 1:
        return AssignmentType.OneGroup
      default:
        return undefined
    }
  }

  @computed
  get groupingType(): AssignmentGroupingType | undefined {
    if (!this.hasData) return undefined
    // return index of the rubric type enum
    switch (this.data.groupingType) {
      case 0:
        return AssignmentGroupingType.manual
      case 1:
        return AssignmentGroupingType.automaticRandom
      default:
        return undefined
    }
  }

  @computed
  get hasGrading() {
    return Boolean(this.unsafeData?.assignmentGradingScalars)
  }

  @computed
  /** only used for brightspace as of 11/13/25 */
  get ltiAssignmentNeedsGradingLineItemId() {
    // if assignment has passed config step, no longer validate grading items
    if (!this.isUnconfigured) return false

    // if there are multiple options and we have not selected a valid one
    // we need to prompt user for selection
    if (
      this.data.ltiGradingLineItemOptions &&
      this.data.ltiGradingLineItemOptions.length > 1 &&
      (!this.data.ltiGradingLineItemId ||
        !this.data.ltiGradingLineItemOptions.some(
          (option) => option.id === this.data.ltiGradingLineItemId
        ))
    )
      return true

    return false
  }

  @computed
  get maxGroupSize() {
    return this.data?.groupingSizeMaximum ?? GROUPING_SIZE_MAXIMUM_DEFAULT
  }

  @computed
  get desiredGroupSize() {
    return this.data?.groupingSize ?? this.maxGroupSize
  }
}

export enum AssignmentType {
  ManyGroups, // Used to be called studentLed
  OneGroup, // Used to be called instructorLed
}

/// An enum that represents the type of assignment group.
export enum AssignmentGroupingType {
  /// manual
  manual,

  /// automaticRandom
  automaticRandom,
}
